import type { BillingOccurence } from '@/models/BillingOccurence';
import type { ISOCurrency } from '@/models/Currency';
import type { ExperienceLevel } from '@/models/ExperienceLevel';
import type { TranslationString } from '@/models/Language';
import type { PlanCategory } from '@/models/PlanCategory';
import type { SignupType } from '@/models/SignupType';
import type { PaymentMethod } from '@/utilities/PaymentMethods';

export interface TrackingContext {
	user_id: string | null;
	portal: string;
	signup_type: SignupType;
	locale: string;
}

export type PurchaseType = 'monthly' | 'quarterly' | 'biannually' | 'yearly';

export const purchaseTypes: Record<BillingOccurence, PurchaseType> = {
	Monthly: 'monthly',
	Trimonthly: 'quarterly',
	Biannually: 'biannually',
	Yearly: 'yearly',
};

export type PaymentType = 'paypal' | 'creditcard' | 'debit';

export const paymentTypes: Record<PaymentMethod, PaymentType> = {
	'PayPal:Reepay': 'paypal',
	'CreditCard:Adyen': 'creditcard',
	'Debit:Heidelpay': 'debit',
};

export type CouponCodeType = 'voucher' | 'discount_code';

export interface TrackingEventMap {
	checkout_started: Record<string, never>;
	checkout_choose_course: {
		value: number;
		currency: ISOCurrency;
		level: ExperienceLevel;
		instrument?: PlanCategory;
		purchase_type: PurchaseType;
	};
	checkout_choose_payment: {
		payment_type: PaymentType;
	};
	coupon_applied: {
		code_type: CouponCodeType;
	};
	purchase: {
		ecommerce: {
			transaction_id: string;
			value: number;
			currency: ISOCurrency;
			coupon?: 'discount_code';
			items: {
				item_id: string | undefined;
				item_name: string;
				item_category: PlanCategory;
				price: number;
				quantity: number;
			}[];
		};
		payment_type: PaymentType;
		purchase_type: PurchaseType;
	};
}

export interface PurchaseCheckout {
	value: number;
	currency: ISOCurrency;
	coupon: 'discount_code' | null;
	paymentType: PaymentType;
	portal: string;
	signupType: SignupType;
}

export type TrackingEventName = keyof TrackingEventMap;

// The old GTM container reads these keys, so the payloads must not change until the events are removed.
export interface LegacyTrackingEventMap {
	signupTracker: {
		eventTrigger: string;
		stepNr: number;
		stepName: TranslationString;
		instrument: PlanCategory | null;
		plan: string;
		payment: string;
		experienceLevel: string;
	};
	registrationComplete: {
		signupMethod: string;
	};
	transactionComplete: {
		transactionId: string;
		transactionAffiliation: string;
		transactionTotal: number;
		transactionTax: string;
		transactionValue: string;
		transactionProducts: {
			sku: string | undefined;
			name: string;
			category: string;
			price: number;
			quantity: number;
		}[];
	};
	locale: {
		locale: string;
	};
	ab_branch: {
		ab_branch: string;
	};
}

export type LegacyTrackingEventName = keyof LegacyTrackingEventMap;
